import React from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { 
  Ban, 
  ShieldAlert, 
  Lock, 
  Terminal, 
  UserPlus, 
  AlertCircle,
  ChevronRight,
  ArrowLeft,
  XCircle,
  Gavel
} from 'lucide-react';

const AcceptableUsePolicy: React.FC = () => {
  return (
    <div className="min-h-screen pt-24 pb-20 bg-white relative overflow-hidden font-sans">
      <div className="absolute top-0 left-0 w-full h-[600px] bg-[radial-gradient(circle_at_20%_0%,rgba(0,0,0,0.03)_0%,transparent_70%)] -z-10" />
      
      <div className="max-w-5xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex flex-col items-start gap-4 mb-12"
        >
          <Link to="/" className="inline-flex items-center gap-2 text-[10px] font-bold text-zinc-400 hover:text-black transition-all group uppercase tracking-[0.2em]">
            <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
            Home
          </Link>
          
          <div className="space-y-2">
            <h1 className="text-3xl md:text-5xl font-black font-display text-zinc-950 tracking-tighter uppercase">
              Acceptable <span className="italic text-zinc-400 font-heavy">Use</span>
            </h1>
            <p className="text-zinc-500 font-medium text-sm leading-relaxed max-w-xl">
              Guidelines for a safe, high-performance simulation environment.
            </p>
          </div>

          <div className="flex items-center gap-4 text-[9px] font-bold uppercase tracking-[0.3em] text-zinc-400 pt-2 border-t border-zinc-100 w-full">
            <span>Last Updated: May 19, 2026</span>
            <span className="w-1 h-1 rounded-full bg-zinc-200" />
            <span>Ref: AUP-V1</span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-8">
          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-[2rem] border border-zinc-100 shadow-xl shadow-zinc-900/5 group"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-zinc-100 rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-zinc-100/20 border border-zinc-200">
                <Ban className="w-7 h-7 text-zinc-950" />
              </div>
              <div className="space-y-6 w-full">
                <h2 className="text-xl font-heavy font-display text-zinc-950 uppercase tracking-tight">1. Prohibited Media Content</h2>
                <div className="space-y-4 text-zinc-600 font-medium leading-relaxed text-sm">
                  <p>
                    Users are strictly prohibited from uploading any media (audio, MIDI, images) that violates the following criteria:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { label: 'Piracy', desc: 'Distributing unauthorized copies or pirated software/media.' },
                      { label: 'Abusive', desc: 'Hate speech, harassment, or explicit/vulgar content.' },
                      { label: 'Illegal', desc: 'Any content that promotes illegal acts or violates local laws.' }
                    ].map((item, i) => (
                      <div key={i} className="p-5 bg-zinc-50 rounded-2xl border border-zinc-100 space-y-2">
                        <XCircle className="w-5 h-5 text-zinc-400" />
                        <span className="block text-[10px] font-black uppercase text-zinc-950 tracking-wider italic">{item.label}</span>
                        <p className="text-xs opacity-80 leading-relaxed font-bold">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.section>

          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-cyan-50/20 p-8 md:p-10 rounded-[2rem] border border-cyan-100 shadow-xl shadow-cyan-900/5"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start text-zinc-600">
              <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-cyan-600/20">
                <Terminal className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-4 w-full">
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight">2. System Integrity & Security</h2>
                <p className="text-sm italic font-medium">To protect the creator cloud environment, the following actions are prohibited:</p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  {[
                    'Reverse engineering simulation logic or engine assets.',
                    'Automated scraping of user projects or engine code.',
                    'Uploading malware, logic bombs, or infected files.',
                    'Circumventing resolution or storage limits.',
                    'Exploiting engine glitches for unauthorized access.',
                    'DDoS or any attempts to degrade cloud rendering performance.'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-4 p-4 bg-white rounded-2xl border border-cyan-100 items-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 shrink-0" />
                      <span className="text-xs font-bold text-cyan-950 tracking-tight">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.section>

          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-[2rem] border border-zinc-100 shadow-xl shadow-zinc-900/5 group"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start text-zinc-600 font-medium">
              <div className="w-14 h-14 bg-zinc-100 rounded-2xl flex items-center justify-center shrink-0 border border-zinc-200">
                <UserPlus className="w-7 h-7 text-zinc-950" />
              </div>
              <div className="space-y-6">
                <h2 className="text-xl font-heavy font-display text-zinc-950 uppercase tracking-tight">3. Account Abuse</h2>
                <div className="space-y-4 text-sm leading-relaxed">
                  <p>Account sharing is strictly forbidden for Single-User licenses. Creating multiple accounts to bypass trial or free-tier limitations will result in an immediate <strong className="text-zinc-950">permanent hardware/IP ban</strong>.</p>
                </div>
              </div>
            </div>
          </motion.section>

          <motion.section 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="pt-8"
          >
            <div className="bg-white p-10 md:p-14 rounded-[3rem] border border-zinc-100 shadow-2xl shadow-zinc-950/5 relative overflow-hidden font-sans">
              <div className="absolute top-0 right-0 w-96 h-96 bg-zinc-500/5 rounded-full blur-[100px] -z-0" />
              <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-3xl font-heavy font-display text-zinc-950 uppercase tracking-tight leading-none italic font-black">
                    Enforcement <br />
                    <span className="text-zinc-400">Policy</span>
                  </h2>
                  <p className="text-zinc-500 font-medium text-xs leading-relaxed max-w-sm">
                    We reserve the right to suspend or terminate any account that violates these policies without notice or refund.
                  </p>
                </div>
                <div className="bg-cyan-50 p-8 rounded-[2rem] border border-cyan-100">
                  <div className="flex items-center gap-4 mb-4">
                    <Gavel className="w-6 h-6 text-cyan-600" />
                    <span className="text-[10px] font-black uppercase tracking-[0.3em] text-cyan-400">Legal Authority</span>
                  </div>
                  <p className="text-xs font-bold leading-relaxed uppercase tracking-tighter italic text-cyan-950">
                    FUN CIRCLE GAMES IS THE SOLE ARBITER OF WHAT CONSTITUTES A VIOLATION OF THESE POLICIES.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default AcceptableUsePolicy;
